import { useEffect, useRef } from 'react';
import './BubbleBeamAnimation.css';

export default function BubbleBeamAnimation({ playerRef, opponentRef, onComplete }) {
  const animRef = useRef(null);

  useEffect(() => {
    const attackerEl = playerRef.current;
    const defenderEl = opponentRef.current;
    const container = defenderEl.closest(".battle-container");
    
    if (!attackerEl || !defenderEl || !container) {
      onComplete();
      return;
    }

    // Get positions for both Pokemon
    const attackerRect = attackerEl.getBoundingClientRect();
    const defenderRect = defenderEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Calculate centers
    const attackerCenterX = attackerRect.left - containerRect.left + attackerRect.width / 2;
    const attackerCenterY = attackerRect.top - containerRect.top + attackerRect.height / 2;
    const defenderCenterX = defenderRect.left - containerRect.left + defenderRect.width / 2;
    const defenderCenterY = defenderRect.top - containerRect.top + defenderRect.height / 2;

    // Calculate direction for bubble beam
    const dx = defenderCenterX - attackerCenterX;
    const dy = defenderCenterY - attackerCenterY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const angle = Math.atan2(dy, dx);

    // Track all created elements for cleanup
    const elements = [];
    const timers = [];
    
    // PHASE 1: PREPARATION AND BEAM CHARGING (0-400ms)
    // Initial attacker animation - beam preparation
    attackerEl.style.transition = 'transform 0.4s ease-in-out, filter 0.4s ease-in-out';
    attackerEl.style.transform = 'scale(1.1)';
    attackerEl.style.filter = 'brightness(1.3) drop-shadow(0 0 15px #00BFFF)';
    
    // Create beam charging aura around attacker
    const beamAura = document.createElement('div');
    beamAura.className = 'bubble-beam-charging-aura';
    beamAura.style.left = `${attackerCenterX}px`;
    beamAura.style.top = `${attackerCenterY}px`;
    container.appendChild(beamAura);
    elements.push(beamAura);
    
    // Create water pressure buildup particles around attacker
    for (let i = 0; i < 12; i++) {
      const pressureTimer = setTimeout(() => {
        const pressure = document.createElement('div');
        pressure.className = 'bubble-beam-pressure-particle';
        
        const pressureAngle = (i / 12) * Math.PI * 2;
        const pressureDistance = 25 + Math.random() * 20;
        pressure.style.left = `${attackerCenterX + Math.cos(pressureAngle) * pressureDistance}px`;
        pressure.style.top = `${attackerCenterY + Math.sin(pressureAngle) * pressureDistance}px`;
        pressure.style.setProperty('--pressure-delay', `${i * 0.03}s`);
        
        container.appendChild(pressure);
        elements.push(pressure);
        
        // Auto-remove pressure particle
        const pressureRemoveTimer = setTimeout(() => {
          if (pressure.isConnected) {
            pressure.remove();
          }
        }, 800);
        timers.push(pressureRemoveTimer);
      }, i * 25);
      timers.push(pressureTimer);
    }

    // PHASE 2: BUBBLE BEAM ATTACK (400-1200ms)
    const beamAttackTimer = setTimeout(() => {
      // Create multiple beam segments instead of single rotated beam
      const beamSegments = 8;
      for (let s = 0; s < beamSegments; s++) {
        const segmentProgress = s / beamSegments;
        const segmentX = attackerCenterX + Math.cos(angle) * distance * segmentProgress;
        const segmentY = attackerCenterY + Math.sin(angle) * distance * segmentProgress;
        
        const beamSegment = document.createElement('div');
        beamSegment.className = 'bubble-beam-segment';
        beamSegment.style.left = `${segmentX}px`;
        beamSegment.style.top = `${segmentY}px`;
        beamSegment.style.setProperty('--segment-delay', `${s * 50}ms`);
        
        container.appendChild(beamSegment);
        elements.push(beamSegment);
        
        // Remove segment after animation
        const segmentTimer = setTimeout(() => {
          if (beamSegment.isConnected) {
            beamSegment.remove();
          }
        }, 1000);
        timers.push(segmentTimer);
      }
      
      // Create concentrated bubble stream particles along the beam
      const totalBeamBubbles = 25;
      for (let i = 0; i < totalBeamBubbles; i++) {
        const bubbleTimer = setTimeout(() => {
          const beamBubble = document.createElement('div');
          beamBubble.className = 'bubble-beam-particle';
          beamBubble.style.left = `${attackerCenterX}px`;
          beamBubble.style.top = `${attackerCenterY}px`;
          
          // Calculate position along the beam path using the same angle as the beam
          const progress = 0.15 + (i / totalBeamBubbles) * 0.7; // 15% to 85% along beam
          const beamDistance = distance * progress;
          
          // Use the same angle calculation as the beam stream
          const beamX = Math.cos(angle) * beamDistance;
          const beamY = Math.sin(angle) * beamDistance;
          
          // Add minimal random spread to keep bubbles aligned with beam
          const spreadX = beamX + (Math.random() - 0.5) * 8; // Reduced spread
          const spreadY = beamY + (Math.random() - 0.5) * 8;
          
          const bubbleSize = 12 + Math.random() * 16; // 12-28px
          const travelTime = 400 + Math.random() * 150; // 400-550ms
          
          beamBubble.style.setProperty('--beam-target-x', `${spreadX}px`);
          beamBubble.style.setProperty('--beam-target-y', `${spreadY}px`);
          beamBubble.style.setProperty('--beam-bubble-size', `${bubbleSize}px`);
          beamBubble.style.setProperty('--beam-travel-time', `${travelTime}ms`);
          beamBubble.style.setProperty('--beam-rotation', `${(Math.random() - 0.5) * 180}deg`); // Reduced rotation
          
          container.appendChild(beamBubble);
          elements.push(beamBubble);
          
          // Remove beam bubble after travel
          const beamBubbleTimer = setTimeout(() => {
            if (beamBubble.isConnected) {
              beamBubble.remove();
            }
          }, travelTime + 100);
          timers.push(beamBubbleTimer);
        }, i * 20); // Rapid-fire bubble stream
        timers.push(bubbleTimer);
      }
      
      // Create beam impact effect particles
      for (let i = 0; i < 8; i++) {
        const impactTimer = setTimeout(() => {
          const impact = document.createElement('div');
          impact.className = 'bubble-beam-impact-particle';
          
          const impactSpread = 20;
          impact.style.left = `${defenderCenterX + (Math.random() - 0.5) * impactSpread}px`;
          impact.style.top = `${defenderCenterY + (Math.random() - 0.5) * impactSpread}px`;
          impact.style.setProperty('--impact-delay', `${i * 0.05}s`);
          
          container.appendChild(impact);
          elements.push(impact);
          
          const impactRemoveTimer = setTimeout(() => {
            if (impact.isConnected) {
              impact.remove();
            }
          }, 600);
          timers.push(impactRemoveTimer);
        }, 400 + i * 30);
        timers.push(impactTimer);
      }
      
    }, 400);
    timers.push(beamAttackTimer);

    // PHASE 3: POWERFUL IMPACT AND SPEED REDUCTION (1200-1800ms)
    const impactTimer = setTimeout(() => {
      // Create powerful bubble beam impact explosion
      const beamImpact = document.createElement('div');
      beamImpact.className = 'bubble-beam-impact-explosion';
      beamImpact.style.left = `${defenderCenterX}px`;
      beamImpact.style.top = `${defenderCenterY}px`;
      container.appendChild(beamImpact);
      elements.push(beamImpact);
      
      // Speed reduction chance (10%)
      const speedReductionChance = Math.random();
      if (speedReductionChance < 0.1) {
        const speedReduction = document.createElement('div');
        speedReduction.className = 'bubble-beam-speed-reduction';
        speedReduction.style.left = `${defenderCenterX}px`;
        speedReduction.style.top = `${defenderCenterY - 50}px`;
        container.appendChild(speedReduction);
        elements.push(speedReduction);
        
        // Enhanced impact for speed reduction
        beamImpact.classList.add('speed-reduction-impact');
      }
      
      // Make defender react with powerful hit animation
      defenderEl.classList.add('bubble-beam-hit');
      defenderEl.style.filter = 'brightness(1.4) hue-rotate(200deg) saturate(1.6)';
      
      // Create powerful water burst particles
      for (let i = 0; i < 20; i++) {
        const burst = document.createElement('div');
        burst.className = 'bubble-beam-water-burst';
        
        const burstAngle = Math.random() * Math.PI * 2;
        const burstDistance = 20 + Math.random() * 45;
        burst.style.left = `${defenderCenterX + Math.cos(burstAngle) * burstDistance}px`;
        burst.style.top = `${defenderCenterY + Math.sin(burstAngle) * burstDistance}px`;
        
        burst.style.setProperty('--burst-rotation', `${Math.random() * 360}deg`);
        burst.style.setProperty('--burst-scale', `${0.7 + Math.random() * 0.9}`);
        burst.style.setProperty('--burst-drift-x', `${(Math.random() - 0.5) * 50}px`);
        burst.style.setProperty('--burst-drift-y', `${(Math.random() - 0.5) * 50}px`);
        
        container.appendChild(burst);
        elements.push(burst);
        
        const burstTimer = setTimeout(() => {
          if (burst.parentNode) {
            burst.remove();
          }
        }, 700 + Math.random() * 300);
        timers.push(burstTimer);
      }
      
      // Create multiple expanding water shock waves
      for (let i = 0; i < 3; i++) {
        const shockTimer = setTimeout(() => {
          const shockwave = document.createElement('div');
          shockwave.className = 'bubble-beam-shockwave';
          shockwave.style.left = `${defenderCenterX}px`;
          shockwave.style.top = `${defenderCenterY}px`;
          shockwave.style.setProperty('--shockwave-delay', `${i * 0.1}s`);
          
          container.appendChild(shockwave);
          elements.push(shockwave);
          
          const shockRemoveTimer = setTimeout(() => {
            if (shockwave.isConnected) {
              shockwave.remove();
            }
          }, 1000);
          timers.push(shockRemoveTimer);
        }, i * 100);
        timers.push(shockTimer);
      }
      
      // Reset defender after hit effect
      const defenderResetTimer = setTimeout(() => {
        defenderEl.classList.remove('bubble-beam-hit');
        defenderEl.style.filter = '';
      }, 700);
      timers.push(defenderResetTimer);
      
    }, 1200); // Impact timing
    timers.push(impactTimer);
    
    // Reset attacker position after animation
    const resetTimer = setTimeout(() => {
      attackerEl.style.transition = 'transform 0.3s ease-out, filter 0.3s ease-out';
      attackerEl.style.transform = '';
      attackerEl.style.filter = '';
    }, 1400);
    timers.push(resetTimer);

    // Complete animation and cleanup
    const completeTimer = setTimeout(() => {
      onComplete();
    }, 1900);
    timers.push(completeTimer);
    
    // Cleanup function
    return () => {
      // Clear all timers
      timers.forEach(timer => clearTimeout(timer));
      
      // Reset attacker
      if (attackerEl) {
        attackerEl.style.transition = '';
        attackerEl.style.transform = '';
        attackerEl.style.filter = '';
      }
      
      // Reset defender
      if (defenderEl) {
        defenderEl.classList.remove('bubble-beam-hit');
        defenderEl.style.filter = '';
      }
      
      // Remove all created elements
      elements.forEach(element => {
        if (element && element.parentNode) {
          element.remove();
        }
      });
      
      // Remove any remaining effects
      const remainingElements = container.querySelectorAll(
        '.bubble-beam-charging-aura, .bubble-beam-pressure-particle, .bubble-beam-segment, ' +
        '.bubble-beam-particle, .bubble-beam-impact-particle, .bubble-beam-impact-explosion, ' +
        '.bubble-beam-speed-reduction, .bubble-beam-water-burst, .bubble-beam-shockwave'
      );
      remainingElements.forEach(el => el.remove());
    };
  }, [playerRef, opponentRef, onComplete]);
  
  return null;
}

BubbleBeamAnimation.moveType = "water";
